// File: src/components/mixer/Color Mixer UI Components/CodeInputRow.jsx
import React, { useEffect, useRef, useState } from 'react';

const S = {
  container: {
    boxSizing: 'border-box',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',       // ⬅️ ensures centering
    justifyContent: 'center',
    padding: '0 12px 96px',
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },

  dividerRow: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '.72rem',
    color: '#6b7280',
    fontWeight: 800,
    letterSpacing: '.08em',
    textTransform: 'uppercase',
    margin: '8px 0 12px',
    width: '100%',
    maxWidth: 400,             // ⬅️ keep divider centered, same width as inputs
  },
  dividerLine: { flex: 1, height: 1, backgroundColor: '#e5e7eb' },
  dividerText: { margin: '0 8px' },

  boxRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: 10,
    margin: '12px 0 8px',
    width: '100%',
    maxWidth: 400,             // ⬅️ input row width constrained and centered
  },

  inputBox: {
    width: 52,
    height: 60,
    borderRadius: 12,
    background: '#fff',
    border: '1px solid #e5e7eb',
    textAlign: 'center',
    fontSize: '1.5rem',
    fontWeight: 900,
    color: '#0f172a',
    textTransform: 'uppercase',
    fontVariantNumeric: 'tabular-nums',
    outline: 'none',
    transition: 'box-shadow .14s ease, border-color .14s ease',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
  },

  readOnlyBox: {
    width: 52,
    height: 60,
    borderRadius: 12,
    background: '#f8fafc',
    display: 'flex',                // ⬅ enable flexbox
    alignItems: 'center',           // ⬅ vertical center
    justifyContent: 'center',       // ⬅ horizontal center
    fontSize: '1.5rem',
    fontWeight: 900,
    color: '#9ca3af',
    textTransform: 'uppercase',
    fontVariantNumeric: 'tabular-nums',
  },
  

  helper: {
    fontSize: '.75rem',
    lineHeight: 1.5,
    color: '#9ca3af',
    maxWidth: 300,
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: 10,
    marginBottom: 16,
    lineHeight: 1.4,
    textAlign: 'center',
    maxWidth: 300,
  },

  actionBar: {
    position: 'fixed',
    inset: 'auto 0 0 0',
    background: '#fff',
    borderTop: '1px solid #eef0f2',
    display: 'flex',
    gap: 10,
    padding: '12px 16px calc(12px + env(safe-area-inset-bottom, 0px))',
    boxSizing: 'border-box',
    maxWidth: 760,
    margin: '0 auto',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '100%',
    zIndex: 50,
  },
  button: {
    flex: 1,
    height: 44,
    borderRadius: 12,
    border: '1px solid #0b0b0b',
    background: '#0b0b0b',
    color: '#fff',
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  buttonDisabled: {
    borderColor: '#d1d5db',
    background: '#d1d5db',
    color: '#fff',
    cursor: 'not-allowed',
  },
};

export default function CodeInputRow({
  code = '',
  onChange,
  onSubmit,
  showSubmit = false,
  isReadOnly = false,
  shouldShake = false,
  onShakeComplete,
}) {
  const inputs = [useRef(null), useRef(null), useRef(null)];
  const [values, setValues] = useState(['', '', '']);

  useEffect(() => {
    if (isReadOnly && code.length === 3) setValues(code.toUpperCase().split(''));
  }, [code, isReadOnly]);

  useEffect(() => {
    if (!isReadOnly) inputs[0].current?.focus();
  }, [isReadOnly]);

  useEffect(() => {
    if (shouldShake) {
      setValues(['', '', '']);
      setTimeout(() => {
        inputs[0].current?.focus();
        onShakeComplete?.();
      }, 600);
    }
  }, [shouldShake, onShakeComplete]);

  const sanitizeChar = (c) => (c || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 1);

  const handlePaste = (e) => {
    if (isReadOnly) return;
    const text = (e.clipboardData?.getData('text') || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!text) return;
    e.preventDefault();
    const arr = text.slice(0, 3).padEnd(3, '').split('');
    setValues(arr);
    onChange?.(arr.join(''));
    inputs[Math.min(arr.findIndex((c) => !c), 2) || 2]?.current?.focus();
  };

  const handleChange = (idx, e) => {
    if (isReadOnly) return;
    const val = sanitizeChar(e.target.value);
    const next = values.map((v, i) => (i === idx ? val : v));
    setValues(next);
    onChange?.(next.join(''));
    if (val && idx < 2) inputs[idx + 1].current?.focus();
  };

  const handleKeyDown = (idx, e) => {
    if (isReadOnly) return;
    if (e.key === 'Backspace') {
      e.preventDefault();
      const next = [...values];
      if (next[idx]) {
        next[idx] = '';
        setValues(next);
        onChange?.(next.join(''));
        return;
      }
      if (idx > 0) {
        inputs[idx - 1].current?.focus();
        const prev = [...values];
        prev[idx - 1] = '';
        setValues(prev);
        onChange?.(prev.join(''));
      }
    }
    if (e.key.length === 1) {
      const v = sanitizeChar(e.key);
      if (v) {
        e.preventDefault();
        const next = [...values];
        next[idx] = v;
        setValues(next);
        onChange?.(next.join(''));
        if (idx < 2) inputs[idx + 1].current?.focus();
      }
    }
  };

  const canSubmit = values.every((v) => v.length === 1);

  return (
    <div style={S.container}>
      <div style={S.dividerRow} aria-hidden>
        <div style={S.dividerLine} />
        <div style={S.dividerText}>Enter partner code</div>
        <div style={S.dividerLine} />
      </div>

      <div style={{ ...S.boxRow, animation: shouldShake ? 'codeShake 0.6s' : 'none' }}>
        {values.map((digit, idx) =>
          isReadOnly ? (
            <div key={idx} style={S.readOnlyBox}>
              {digit || '0'}
            </div>
          ) : (
            <input
              key={idx}
              type="text"
              inputMode="text"
              autoComplete="off"
              maxLength={1}
              placeholder="0"
              ref={inputs[idx]}
              value={digit}
              onChange={(e) => handleChange(idx, e)}
              onKeyDown={(e) => handleKeyDown(idx, e)}
              onPaste={idx === 0 ? handlePaste : undefined}
              style={S.inputBox}
            />
          )
        )}
      </div>

      <div style={S.helper}>
        Input your partner’s code here, or share this code with your partner.
      </div>

      {showSubmit && (
        <div style={S.actionBar}>
          <button
            onClick={onSubmit}
            disabled={!canSubmit}
            style={{
              ...S.button,
              ...(canSubmit ? {} : S.buttonDisabled),
            }}
          >
            Enter
          </button>
        </div>
      )}

      <style>
        {`
          @keyframes codeShake {
            0%, 100% { transform: translateX(0); }
            10%, 90%  { transform: translateX(-4px); }
            20%, 80%  { transform: translateX(4px); }
            30%, 50%, 70% { transform: translateX(-8px); }
            40%, 60%  { transform: translateX(8px); }
          }
        `}
      </style>
    </div>
  );
}
